import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getUserGymId } from "@/utils/gymAuth";
import {
  getGymTrialLink,
  regenerateGymTrialLink,
  setGymTrialLinkEnabled,
  PublicTrialError,
  type GymTrialLink,
} from "@/services/edgeFunctionWrappers/publicTrialsWrapper";

export function TrialLinkTab() {
  const { t } = useTranslation(["settings", "common"]);
  const gymId = getUserGymId();
  const [link, setLink] = useState<GymTrialLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    if (!gymId) return;
    setLoading(true);
    setError(null);
    try {
      setLink(await getGymTrialLink(gymId));
    } catch (e) {
      setError(e instanceof PublicTrialError && e.code === "FORBIDDEN" ? t("trialLink.adminOnly") : t("trialLink.error"));
    } finally {
      setLoading(false);
    }
  }, [gymId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const url = link ? `${window.location.origin}/trial/${link.token}` : "";

  const run = async (fn: () => Promise<GymTrialLink>, success?: string) => {
    if (!gymId) return;
    setBusy(true);
    try {
      setLink(await fn());
      if (success) toast.success(success);
    } catch {
      toast.error(t("trialLink.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("trialLink.copied"));
    } catch {
      toast.error(t("trialLink.saveError"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("trialLink.title")}</CardTitle>
        <CardDescription>{t("trialLink.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!gymId ? (
          <p className="text-sm text-muted-foreground">{t("trialLink.noGym")}</p>
        ) : loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="space-y-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={load}>{t("trialLink.retry")}</Button>
          </div>
        ) : !link ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("trialLink.none")}</p>
            <Button disabled={busy} onClick={() => run(() => regenerateGymTrialLink(gymId), t("trialLink.regenerated"))}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("trialLink.create")}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="trial-enabled">{t("trialLink.enabled")}</Label>
                <p className="text-sm text-muted-foreground">{t("trialLink.enabledHint")}</p>
              </div>
              <Switch
                id="trial-enabled"
                checked={link.is_enabled}
                disabled={busy}
                onCheckedChange={(v) => run(() => setGymTrialLinkEnabled(gymId, v))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trial-url">{t("trialLink.link")}</Label>
              <Input id="trial-url" readOnly value={url} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button variant="outline" onClick={copy}><Copy className="mr-2 h-4 w-4" />{t("trialLink.copy")}</Button>
                <Button variant="outline" asChild>
                  <a href={url} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />{t("trialLink.open")}</a>
                </Button>
                <Button variant="destructive" disabled={busy} onClick={() => setConfirmOpen(true)}>
                  <RefreshCw className="mr-2 h-4 w-4" />{t("trialLink.regenerate")}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("trialLink.regenerateTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("trialLink.regenerateDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel", { defaultValue: "Annuler" })}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => gymId && run(() => regenerateGymTrialLink(gymId), t("trialLink.regenerated"))}
            >
              {t("trialLink.regenerateConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
